import type { Middleware, MiddlewareAPI } from "redux";
import { encode, decode } from 'msgpack-lite';
import { refreshToken } from "../../utils/kbt-api";

import type {
  AppActions,
  TWSStoreActions,
} from "./types";

export const socketMiddleware = (
  wsActions: TWSStoreActions,
): Middleware => {
  return (store) => {
    let socket: WebSocket | null = null;
    let socketUrl: string | null = null;

    let isConnected = false;
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
    let isInternalCloseDispatch = false;

    const reconnectDelayMs = 3000;

    const connectSocket = async (url: string, dispatch: MiddlewareAPI['dispatch']) => {
      socketUrl = url;

      const token = await refreshToken().then((data) => data.access_token).catch(() => null);

      if (token) {
        url = `${url}?token=${token}`;
      }

      socket = new WebSocket(url);
      socket.binaryType = 'arraybuffer';

      console.log('WebSocket connection initiated:', url);

      socket.onopen = () => {
        isConnected = true;
        dispatch({ type: wsActions.onOpen });
      };

      socket.onerror = () => {
        dispatch({ type: wsActions.onError, payload: 'WebSocket error' });
      };

      socket.onmessage = (event) => {
        console.log('Received message:', event.data);

        const data = decode(new Uint8Array(event.data));
        const parsedUrl = new URL(url);
        const protocol = parsedUrl.protocol;
        const host = parsedUrl.host;
        const path = parsedUrl.pathname;

        dispatch({ type: wsActions.onMessage, payload: { ...data, url: `${protocol}//${host}${path}` } });
      };

      socket.onclose = (event) => {
        isConnected = false;
        socket = null;

        isInternalCloseDispatch = true;
        dispatch({ type: wsActions.onClose });
        isInternalCloseDispatch = false;

        if (event.code === 1008 || event.code === 4401 || event.code === 4403) {
          dispatch({ type: wsActions.onError, payload: 'WebSocket unauthorized, retrying...' });
        }

        if (reconnectTimer) {
          clearTimeout(reconnectTimer);
        }

        if (socketUrl) {
          reconnectTimer = setTimeout(() => {
            dispatch({ type: wsActions.wsInit, payload: socketUrl });
          }, reconnectDelayMs);
        }
      };
    };

    return (next) => (action: AppActions) => {
      const { dispatch } = store;
      const { type } = action;
      const { wsInit, wsSendMessage, onClose } = wsActions;
      const payloadUrl = action.payload;

      if (type === wsInit && !isConnected && !socket && typeof payloadUrl === 'string') {
        connectSocket(payloadUrl, dispatch);
      }

      if (socket) {
        if (type === wsSendMessage && socket.readyState === WebSocket.OPEN) {
          socket.send(encode(action.payload));
        }

        if (type === onClose && !isInternalCloseDispatch) {
          if (reconnectTimer) {
            clearTimeout(reconnectTimer);
          }
          isConnected = false;
          reconnectTimer = null;
          socketUrl = null;
          socket.close();
          socket = null;
        }
      }

      next(action);
    };
  };
};
